import React, { useMemo, useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import TableContainer from 'Common/TableContainer';
import { Link } from 'react-router-dom';
import { MoreHorizontal, Eye, FileEdit, Trash2 } from 'lucide-react';
import { Dropdown } from 'Common/Components/Dropdown';
import filterDataBySearch from 'Common/filterDataBySearch';
import { getShipment } from 'slices/thunk';
import { RootState } from 'slices';

const Shipment = () => {
    const dispatch = useDispatch();
    const shipment = useSelector((state: RootState) => state.Ecommerce.shipment);

    const [data, setData] = useState<any[]>([]);

    useEffect(() => {
        dispatch(getShipment() as any);
    }, [dispatch]);

    useEffect(() => {
        if (shipment && Array.isArray(shipment)) {
            const mapped = shipment.map((item: any, index: number) => ({
                id: index + 1,
                receiverName: item.receiverName,
                shipperName: item.shipperName,
                orderDate: new Date(item.delivery).toLocaleDateString(),
                transferCharge: `${item.transferCharge}`,
                quantity: item.Quantity,
                currentLocation: item.currentLocation,
                destination: item.destination,
                totalAmount: item.totalvalue,
                status: item.status.charAt(0).toUpperCase() + item.status.slice(1),
            }));
            setData(mapped);
        }
    }, [shipment]);

    const filterSearchData = (e: any) => {
        const search = e.target.value;
        const keysToSearch = ['orderId', 'receiverName', 'shipperName', 'orderDate', 'transferCharge', 'quantity', 'status'];
        filterDataBySearch(data, search, keysToSearch, setData);
    };

    const columns = useMemo(() => [
        {
            header: "#",
            accessorKey: "id",
            enableColumnFilter: false,
            enableSorting: true,
        },

        {
            header: "Receiving Manager",
            accessorKey: "receiverName",
            enableColumnFilter: false,
            enableSorting: true,
        },
        {
            header: "Shipper Name",
            accessorKey: "shipperName",
            enableColumnFilter: false,
            enableSorting: true,
        },
        {
            header: "Delivery Date",
            accessorKey: "orderDate",
            enableColumnFilter: false,
            enableSorting: true,
        },
        {
            header: "Transfer Charge",
            accessorKey: "transferCharge",
            enableColumnFilter: false,
            enableSorting: true,
        },
        {
            header: "Quantity",
            accessorKey: "quantity",
            enableColumnFilter: false,
            enableSorting: true,
        },
        {
            header: "Total Amount",
            accessorKey: "totalAmount",
            enableColumnFilter: false,
            enableSorting: true,
        },
        {
            header: "Export Location",
            accessorKey: "currentLocation",
            enableColumnFilter: false,
            enableSorting: true,
        },
        {
            header: "Destination",
            accessorKey: "destination",
            enableColumnFilter: false,
            enableSorting: true,
        },
        {
            header: "Status",
            accessorKey: "status",
            enableColumnFilter: false,
            enableSorting: true,
            cell: (cell: any) => {
                const status = cell.row.original.status;
                const statusMap: any = {
                    Recieved: 'green',
                    Shipping: 'purple',
                    New: 'sky',
                    Pending: 'yellow',
                };
                const color = statusMap[status];
                return (
                    <span className={`text-black delivery_status px-2.5 py-0.5 text-xs inline-block font-medium rounded border bg-${color}-200 border-${color}-200 text-${color}-500 dark:bg-${color}-500/20 dark:border-${color}-500/20`}>
                        {status}
                    </span>
                );
            },
        },
        {
            header: "Action",
            enableColumnFilter: false,
            enableSorting: true,
            cell: (cell: any) => (
                <>
                    <Dropdown className="relative">
                        <Dropdown.Trigger id="orderAction5" data-bs-toggle="dropdown" className="flex items-center justify-center size-[30px] dropdown-toggle p-0 text-slate-500 btn bg-slate-100 hover:text-white hover:bg-slate-600 focus:text-white focus:bg-slate-600 focus:ring focus:ring-slate-100 active:text-white active:bg-slate-600 active:ring active:ring-slate-100 dark:bg-slate-500/20 dark:text-slate-400 dark:hover:bg-slate-500 dark:hover:text-white dark:focus:bg-slate-500 dark:focus:text-white dark:active:bg-slate-500 dark:active:text-white dark:ring-slate-400/20">
                            <MoreHorizontal className="size-3"></MoreHorizontal></Dropdown.Trigger>
                        <Dropdown.Content placement={cell.row.index ? "top-end" : "right-end"} className="absolute z-50 py-2 mt-1 ltr:text-left rtl:text-right list-none bg-white rounded-md shadow-md dropdown-menu min-w-[10rem] dark:bg-zink-600" aria-labelledby="orderAction5">
                            <li>
                                <Link className="block px-4 py-1.5 text-base transition-all duration-200 ease-linear text-slate-600 dropdown-item hover:bg-slate-100 hover:text-slate-500 focus:bg-slate-100 focus:text-slate-500 dark:text-zink-100 dark:hover:bg-zink-500 dark:hover:text-zink-200 dark:focus:bg-zink-500 dark:focus:text-zink-200" to="/apps-ecommerce-order-overview"><Eye className="inline-block size-3 ltr:mr-1 rtl:ml-1" /> <span className="align-middle">Overview</span></Link>
                            </li>
                            <li>
                                <Link className="block px-4 py-1.5 text-base transition-all duration-200 ease-linear text-slate-600 dropdown-item hover:bg-slate-100 hover:text-slate-500 focus:bg-slate-100 focus:text-slate-500 dark:text-zink-100 dark:hover:bg-zink-500 dark:hover:text-zink-200 dark:focus:bg-zink-500 dark:focus:text-zink-200" to="#!"><FileEdit className="inline-block size-3 ltr:mr-1 rtl:ml-1" /> <span className="align-middle">Edit</span></Link>
                            </li>
                            <li>
                                <Link className="block px-4 py-1.5 text-base transition-all duration-200 ease-linear text-slate-600 dropdown-item hover:bg-slate-100 hover:text-slate-500 focus:bg-slate-100 focus:text-slate-500 dark:text-zink-100 dark:hover:bg-zink-500 dark:hover:text-zink-200 dark:focus:bg-zink-500 dark:focus:text-zink-200" to="#!"><Trash2 className="inline-block size-3 ltr:mr-1 rtl:ml-1" /> <span className="align-middle">Delete</span></Link>
                            </li>
                        </Dropdown.Content>
                    </Dropdown>

                </>
            ),
        }
    ], [data]);

    return (
        <div className=" col-span-9 card    ">
            <div className="card-body ">
                <div className="grid items-center grid-cols-9 gap-3 mb-5   ">
                    <div className=" col-span-5  ">
                        <h6 className="text-15">Shipments Awaiting Pickup/Delivery</h6>
                    </div>
                    <div className=" col-span-4    ">
                        <div className="flex gap-3">
                            <div className="relative grow ">
                                <input type="text" className="form-input dark:bg-zink-500 dark:border-zink-400" placeholder="Search for ...  " onChange={filterSearchData} />
                            </div>
                            <button type="button" className="btn bg-custom-500 text-white">
                                <i className="ri-download-2-line pr-1"></i> Export
                            </button>
                        </div>
                    </div>
                </div>
                <TableContainer
                    isPagination={true}
                    columns={columns}
                    data={data}
                    customPageSize={7}
                    divclassName="overflow-x-auto"
                    tableclassName="w-full whitespace-nowrap"
                    theadclassName="ltr:text-left rtl:text-right bg-slate-100 text-slate-500 dark:text-zink-200 dark:bg-zink-600"
                    thclassName="px-3.5 py-2.5 first:pl-5 last:pr-5 font-semibold border-y border-slate-200 dark:border-zink-500"
                    tdclassName="px-3.5 py-2.5 first:pl-5 last:pr-5 border-y border-slate-200 dark:border-zink-500"
                    PaginationClassName="flex flex-col items-center mt-5 md:flex-row"
                />
            </div>
        </div>
    );
};

export default Shipment;
